/**
 * PWA "Add to Home Screen" (retention): installed readers come back far more often.
 * Chrome/Edge/Android fire `beforeinstallprompt`, which we capture so we can offer our
 * own install button at a good moment (after the reader has engaged) rather than relying
 * on the browser's hidden default. iOS Safari has no such event, so we detect it and show
 * manual instructions instead.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((fn) => fn());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", ((e: Event) => {
    e.preventDefault(); // stop the mini-infobar; we drive the prompt ourselves
    deferred = e as BeforeInstallPromptEvent;
    notify();
  }) as EventListener);
  window.addEventListener("appinstalled", () => {
    deferred = null;
    try {
      localStorage.setItem("insnip.installed", "1");
    } catch {
      /* ignore */
    }
    notify();
  });
}

/** Already running as an installed app? Then never prompt. */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    (window.navigator as { standalone?: boolean }).standalone === true
  );
}

/** iOS Safari (no beforeinstallprompt), show manual Share → Add to Home Screen steps. */
export function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return /iphone|ipad|ipod/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
}

export function canPromptInstall(): boolean {
  return deferred !== null;
}

export function onInstallChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Trigger the native install prompt. Returns the outcome (or "unavailable"). */
export async function promptInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  if (!deferred) return "unavailable";
  await deferred.prompt();
  const { outcome } = await deferred.userChoice;
  deferred = null;
  notify();
  return outcome;
}
